import { Button, Card, Divider, Flex, Modal, Select, Skeleton, Typography, notification } from 'antd';
import { primitiveColors, spacing } from '../../theming';
import { JobStatusTimeline } from './JobStatusTimeline';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import globalNotification from '../../common/hooks/globalNotification';
import { notifications } from '../../common/constants/notifications';
import { checkForSuperAdmin } from '../../utils/checkRole';
import { sharedReportUrl } from '../../utils/sharedReportUrl';
import { useAuth } from '../../context/AuthContext';
import InfiniteScroll from 'react-infinite-scroll-component';
import { LogsList } from '../../common/components/logsList/LogsList';
import { useJobService } from '../../services/jobService';
import { PreviewJobTabs } from '../../common/components/previewJobTabs';
import DragAndDropButton from '../fileUploadButtons/DragAndDropButton';
import { handleFileUpload } from '../fileUploadButtons/UploadHelper';
import { useMedia } from '../../hooks/useMediaQuery';
import { jobStatusNames } from '../../common/constants/jobStatusNames';
import { Flag } from '../../common/constants/flag';
import { STATUSES_REQUIRING_FILES } from '../../common/constants/jobStatusNames';
import { languageOptions } from '../../common/constants/languageOptions';
import LanguageOption from './LanguageOption';
import { getParserStatusFlag, getReportStatus } from './parserState';
import { getReportReadiness, needsReadyReport } from './reportReadiness';
import { Form } from 'antd';
import { USER_ROLES } from '../../common/constants/localStorage';
import { notificationType } from '../../common/constants/notificationType';

const RECORD_LIMIT_PER_REQUEST = 10;
const baseApiUrl = 'files';

export const JobPreview = ({ job, openEditMode, statuses, handleStatus, onHasUnsavedChanges, hasUnsavedChanges }) => {
    const { currentUser } = useAuth();

    const {
        getJobComments,
        getJobLogs,
        getJobLogsAndComments,
        uploadFile,
        getFilesNames,
        deleteFile,
        changeFileLanguage,
        regenerateReport,
    } = useJobService();
    const { isExtraSmallDevice } = useMedia();
    const { t, i18n } = useTranslation(['jobPreview', 'jobs', 'general']);
    const { id, vin, brand, model, status, language, createdAt, updatedAt, sharedLink } = job;

    const [selectedTab, setSelectedTab] = useState('3');
    const [jobComments, setJobComments] = useState([]);
    const [isCommentInputCollapsed, setIsCommentInputCollapsed] = useState(false);
    const [jobLogs, setJobLogs] = useState([]);

    const [originalJobStatus, setOriginalJobStatus] = useState(status);

    const [jobStatus, setJobStatus] = useState(status);
    const [jobLogsAndComment, setJobLogsAndComments] = useState({
        logsAndComments: [],
        logsCount: 0,
        commentsCount: 0,
    });
    const [files, setFiles] = useState({
        serverFiles: [],
        selectedFiles: {
            report: null,
            kompl: null,
        },
    });
    const [loading, setLoading] = useState({
        fetchingFiles: false,
        uploadingFiles: false,
        deletingFiles: false,
        comments: false,
        logs: false,
    });
    const [hasMore, setHasMore] = useState(true);

    const [form] = Form.useForm();

    // #region Handlers
    const infiniteScrollRef = useRef(null);

    // #region Logs and Comments
    const fetchComments = async () => {
        if (loading.comments) {
            return;
        }

        setLoading(prev => ({ ...prev, comments: true }));
        try {
            const response = await getJobComments(id, jobComments.length, RECORD_LIMIT_PER_REQUEST);

            if (response.comments.length === 0 || response.comments.length < RECORD_LIMIT_PER_REQUEST) {
                setHasMore(false);
            }

            setJobComments(prevComments => [...prevComments, ...response.comments]);
        } catch {
            globalNotification.request({
                type: notifications.type.ERROR,
                resource: notifications.resource.COMMENTS,
                action: notifications.action.FETCH,
                plural: true,
            });
        } finally {
            setLoading(prev => ({ ...prev, comments: false }));
        }
    };

    const fetchLogs = async () => {
        if (loading.logs) {
            return;
        }

        setLoading(prev => ({ ...prev, logs: true }));
        try {
            const response = await getJobLogs(id, jobLogs.length, RECORD_LIMIT_PER_REQUEST);

            if (response.logs.length === 0 || response.logs.length < RECORD_LIMIT_PER_REQUEST) {
                setHasMore(false);
            }

            setJobLogs(prevLogs => [...prevLogs, ...response.logs]);
        } catch {
            globalNotification.request({
                type: notifications.type.ERROR,
                resource: notifications.resource.LOGS,
                action: notifications.action.FETCH,
                plural: true,
            });
        } finally {
            setLoading(prev => ({ ...prev, logs: false }));
        }
    };

    const fetchLogsAndComments = async () => {
        if (loading.comments || loading.logs) {
            return;
        }

        setLoading(prev => ({ ...prev, comments: true, logs: true }));
        try {
            const response = await getJobLogsAndComments(
                id,
                jobLogsAndComment.logsCount,
                RECORD_LIMIT_PER_REQUEST,
                jobLogsAndComment.commentsCount,
                RECORD_LIMIT_PER_REQUEST
            );

            if (response.logsAndComments.length === 0 || response.logsAndComments.length < RECORD_LIMIT_PER_REQUEST) {
                setHasMore(false);
            }

            const { logsAndComments, logsCount, commentsCount } = response;

            setJobLogsAndComments(prevLogsAndComments => {
                return {
                    logsAndComments: [...prevLogsAndComments.logsAndComments, ...logsAndComments],
                    logsCount: prevLogsAndComments.logsCount + logsCount,
                    commentsCount: prevLogsAndComments.commentsCount + commentsCount,
                };
            });
        } catch {
            globalNotification.request({
                type: notifications.type.ERROR,
                resource: notifications.resource.ACTIVITY,
                action: notifications.action.FETCH,
                plural: true,
            });
        } finally {
            setLoading(prev => ({ ...prev, comments: false, logs: false }));
        }
    };
    // #endregion

    // #region Files
    const fetchFilesNames = async () => {
        setLoading(prev => ({ ...prev, fetchingFiles: true }));

        try {
            const response = await getFilesNames(id);

            setFiles(prev => ({
                ...prev,
                serverFiles: response,
            }));
        } catch {
            globalNotification.request({
                type: notifications.type.ERROR,
                resource: notifications.resource.JOBS,
                action: notifications.action.FETCH,
                plural: true,
            });
        } finally {
            setLoading(prev => ({ ...prev, fetchingFiles: false }));
        }
    };

    const handleDeleteFile = async fileName => {
        setLoading(prev => ({ ...prev, deletingFiles: true }));

        try {
            await deleteFile(baseApiUrl, fileName);
            await fetchFilesNames();

            if (fileName.includes(Flag.Report)) {
                setFiles(prev => ({
                    ...prev,
                    selectedFiles: {
                        ...prev.selectedFiles,
                        report: null,
                    },
                }));
            }

            if (fileName.includes(Flag.Kompl)) {
                setFiles(prev => ({
                    ...prev,
                    selectedFiles: {
                        ...prev.selectedFiles,
                        kompl: null,
                    },
                }));
            }

            globalNotification.request({
                type: notifications.type.SUCCESS,
                resource: notifications.resource.JOBS,
                action: notifications.action.DELETE,
                plural: false,
            });
        } catch (error) {
            globalNotification.request({
                type: notifications.type.ERROR,
                resource: notifications.resource.JOBS,
                action: notifications.action.DELETE,
                plural: false,
            });
        } finally {
            setLoading(prev => ({ ...prev, deletingFiles: false }));
        }
    };

    const onClickDelete = fileName => {
        Modal.confirm({
            title: `${t('confirmModal:actions.delete')}?`,
            icon: null,
            styles: { body: { padding: spacing[4] } },
            width: 'fit-content',
            okButtonProps: {
                danger: true,
            },
            onOk: async () => {
                await handleDeleteFile(fileName);
            },
            okText: t('confirmModal:buttons.yes'),
            cancelText: t('confirmModal:buttons.cancel'),
            centered: true,
        });
    };
    // #endregion

    const handleSave = async () => {
        if (!hasUnsavedChanges) {
            return;
        }

        setLoading(prev => ({ ...prev, uploadingFiles: true }));

        try {
            const hasReportFile = files.serverFiles?.some(file => file.fileName.includes(Flag.Report));
            const types = Object.entries(files.selectedFiles)
                .filter(([_, value]) => value !== null)
                .map(([type]) => type);

            if (!hasReportFile && !files.selectedFiles.report && types.includes(Flag.Kompl)) {
                notification.error({
                    message: t('jobPreview:errors.message'),
                    description: t('jobPreview:errors.report-description'),
                    placement: 'top',
                });
                return;
            }

            for (const type of types) {
                // a refused file (e.g. a PDF that asks for a password) stops the save: the status must not move on
                // without it, and the selection stays so the dealer can pick another copy
                const uploaded = await handleFileUpload(
                    baseApiUrl,
                    {
                        file: files.selectedFiles[type],
                        onSuccess: () => {},
                        onProgress: () => {},
                    },
                    uploadFile,
                    'file',
                    job.id,
                    fetchFilesNames,
                    vin,
                    type === Flag.Report ? Flag.Report : Flag.Kompl
                );
                if (uploaded === false) return;
                // saved: a later refusal must not send this one again on the next Save
                setFiles(prev => ({ ...prev, selectedFiles: { ...prev.selectedFiles, [type]: null } }));
            }

            setFiles(prev => ({
                ...prev,
                selectedFiles: {
                    report: null,
                    kompl: null,
                },
            }));

            if (jobStatus.id !== status.id) {
                const isStatusSaved = await handleStatus(job.id, jobStatus, true);

                // the page already explained any failure; keep the status that is actually saved
                if (isStatusSaved !== true) {
                    setJobStatus(originalJobStatus);
                    onHasUnsavedChanges(false);
                    return;
                }
            }

            globalNotification.request({
                type: notifications.type.SUCCESS,
                resource: notifications.resource.JOBS,
                action: notifications.action.UPDATE,
                plural: false,
            });

            if (hasUnsavedChanges) {
                onHasUnsavedChanges(false);
            }

            setOriginalJobStatus(jobStatus);
        } catch (error) {
            globalNotification.request({
                type: notifications.type.ERROR,
                resource: notifications.resource.JOBS,
                action: notifications.action.UPDATE,
                plural: false,
            });
        } finally {
            setLoading(prev => ({ ...prev, uploadingFiles: false }));
        }
    };

    // This function only updates the UI status but doesn't call the server
    // The actual status change will be saved when handleSave is called
    // the files this Preview fetched are newer than the list row's
    const reportReadiness = getReportReadiness(job, files.serverFiles);

    const handleStatusChange = statusId => {
        const newStatus = statuses.find(s => s.id === statusId);

        if (jobStatus?.name === newStatus?.name) {
            return;
        }

        if (job?.status?.name === jobStatusNames.Rejected) {
            globalNotification.open({
                type: notifications.type.ERROR,
                message: t('jobPreview:errors.status.message-2'),
                description: t('jobPreview:errors.status.message-3'),
            });
            return;
        }

        if (newStatus?.name === jobStatusNames.Rejected) {
            Modal.confirm({
                title: t('jobPreview:confirmReject.title'),
                content: t('jobPreview:confirmReject.content'),
                icon: null,
                styles: { body: { padding: spacing[4] } },
                width: 'fit-content',
                okButtonProps: {
                    danger: true,
                },
                centered: true,
                okText: t('confirmModal:buttons.yes'),
                cancelText: t('confirmModal:buttons.cancel'),
                onOk: () => setJobStatus(newStatus),
            });
            return;
        }

        const hasReportFile = files.serverFiles.some(file => file.fileName.includes(Flag.Report));

        if (!hasReportFile && STATUSES_REQUIRING_FILES.includes(newStatus.name)) {
            globalNotification.open({
                type: notifications.type.ERROR,
                message: t('jobPreview:errors.status.message'),
                description: t('jobPreview:errors.status.description'),
            });
            return;
        }

        // SC-QA-53: Verification and Done wait for the report (file uploaded and parse finished)
        if (needsReadyReport(newStatus.name) && reportReadiness !== 'ready') {
            globalNotification.open({
                type: notifications.type.ERROR,
                message: t('jobs:reportReadiness.title'),
                description: t(`jobs:reportReadiness.${reportReadiness}`),
            });
            return;
        }

        // Only update local state, actual saving happens in handleSave
        setJobStatus(newStatus);
        onHasUnsavedChanges(true);
    };
    // #endregion

    // #region useEffect's
    useEffect(() => {
        setHasMore(true);
        setJobLogs([]);
        setJobComments([]);
        setJobLogsAndComments({
            logsAndComments: [],
            logsCount: 0,
            commentsCount: 0,
        });

        switch (selectedTab) {
            case '3':
                fetchLogs();
                break;
            case '2':
                fetchComments();
                break;
            default:
                fetchLogsAndComments();
                break;
        }
    }, [id, selectedTab]);

    useEffect(() => {
        setFiles(prev => ({
            ...prev,
            serverFiles: [],
        }));
        fetchFilesNames();
    }, []);

    useEffect(() => {
        if (files.selectedFiles.report || files.selectedFiles.kompl || jobStatus.id !== status.id) {
            onHasUnsavedChanges(true);
        }
    }, [files.selectedFiles, jobStatus]);
    // #endregion

    const handleScroll = event => {
        if (event.target.scrollTop > 50) {
            setIsCommentInputCollapsed(true);
        } else if (event.target.scrollTop === 0) {
            setIsCommentInputCollapsed(false);
        }
    };

    const handleRegenerateReport = async jobUuid => {
        try {
            await regenerateReport(jobUuid);
            globalNotification.request({
                type: notifications.type.SUCCESS,
                resource: notifications.resource.JOBS,
                action: notifications.action.UPDATE,
            });
            window.location.reload();
        } catch (error) {
            globalNotification.request({
                type: notifications.type.ERROR,
                resource: notifications.resource.JOBS,
                action: notifications.action.UPDATE,
            });
        }
    };

    return (
        <Card style={{ boxShadow: 'none' }} styles={{ body: { padding: 0 } }} bordered={false}>
            <Flex vertical>
                <Flex justify="space-between" align="center">
                    <Typography.Title style={{ margin: 0 }}>{t('title')}</Typography.Title>

                    {currentUser.userRoleNames.includes('Super Admin') && (
                        <Flex gap={spacing[1]}>
                            <Button onClick={() => openEditMode(job)}>{t('edit-info')}</Button>
                            <Button
                                disabled={!job.report?.sharedLink}
                                aria-label="Preview Report"
                                icon={<i className="fi fi-rr-eye" />}
                                onClick={e => {
                                    e.stopPropagation();
                                    window.open(
                                        sharedReportUrl(job.report.sharedLink, i18n.resolvedLanguage),
                                        '_blank'
                                    );
                                }}
                            />
                            <Button
                                disabled={!job.report}
                                icon={<i className="fi fi-rr-refresh" />}
                                onClick={() => handleRegenerateReport(job.uuid)}
                            />
                        </Flex>
                    )}
                </Flex>

                <Divider style={{ margin: isExtraSmallDevice && spacing[2] }} />

                <Flex
                    align={!isExtraSmallDevice && 'center'}
                    vertical={isExtraSmallDevice}
                    justify="space-between"
                    style={{ marginBottom: isExtraSmallDevice ? spacing[1] : spacing[4] }}
                    gap={spacing[1]}
                >
                    <Flex gap={spacing[1]}>
                        <Typography.Text style={{ fontWeight: 500, color: primitiveColors.gray400 }}>
                            {`${t('jobs:fields.created')}:`}
                        </Typography.Text>
                        <Typography.Text style={{ fontWeight: 500, color: primitiveColors.gray700 }}>
                            {createdAt}
                        </Typography.Text>
                    </Flex>

                    <Flex gap={spacing[1]}>
                        <Typography.Text style={{ fontWeight: 500, color: primitiveColors.gray400 }}>
                            {t('jobs:fields.updated')}:
                        </Typography.Text>
                        <Typography.Text style={{ fontWeight: 500, color: primitiveColors.gray700 }}>
                            {updatedAt}
                        </Typography.Text>
                    </Flex>
                </Flex>

                <Flex align={!isExtraSmallDevice && 'center'} vertical={isExtraSmallDevice} gap={spacing[1]}>
                    <Flex vertical gap={spacing[1]}>
                        <Flex gap={spacing[1]}>
                            <Typography.Text style={{ fontWeight: 500, color: primitiveColors.gray400 }}>
                                {`${t('jobs:fields.vin')}:`}
                            </Typography.Text>
                            <Typography.Text style={{ fontWeight: 500, color: primitiveColors.gray700 }}>
                                {vin}
                            </Typography.Text>
                        </Flex>

                        <Flex gap={spacing[1]}>
                            <Typography.Text style={{ fontWeight: 500, color: primitiveColors.gray400 }}>
                                {`${t('jobs:fields.language')}:`}
                            </Typography.Text>
                            <Typography.Text style={{ fontWeight: 500, color: primitiveColors.gray700 }}>
                                {language}
                            </Typography.Text>
                        </Flex>
                    </Flex>

                    <Flex vertical gap={spacing[1]}>
                        <Flex gap={spacing[1]}>
                            <Typography.Text style={{ fontWeight: 500, color: primitiveColors.gray400 }}>
                                {`${t('jobs:fields.brand')}:`}
                            </Typography.Text>
                            <Typography.Text style={{ fontWeight: 500, color: primitiveColors.gray700 }}>
                                {brand}
                            </Typography.Text>
                        </Flex>

                        <Flex gap={spacing[1]}>
                            <Typography.Text style={{ fontWeight: 500, color: primitiveColors.gray400 }}>
                                {`${t('jobs:fields.model')}:`}
                            </Typography.Text>
                            <Typography.Text style={{ fontWeight: 500, color: primitiveColors.gray700 }}>
                                {model}
                            </Typography.Text>
                        </Flex>
                    </Flex>

                    <Flex align={!isExtraSmallDevice} vertical gap={spacing[1]}>
                        <Flex gap={spacing[1]}>
                            <Typography.Text style={{ fontWeight: 500, color: primitiveColors.gray400 }}>
                                {`${t('jobs:fields.isParserError')}:`}
                            </Typography.Text>
                            <Typography.Text style={{ fontWeight: 500, color: primitiveColors.gray700 }}>
                                {t(`jobs:jobErrors.flags.${getParserStatusFlag(job)}`)}
                            </Typography.Text>
                        </Flex>

                        <Flex gap={spacing[1]}>
                            <Typography.Text style={{ fontWeight: 500, color: primitiveColors.gray400 }}>
                                {`${t('jobs:fields.isParserReportReady')}:`}
                            </Typography.Text>
                            <Typography.Text style={{ fontWeight: 500, color: primitiveColors.gray700 }}>
                                {t(`jobs:jobErrors.flags.${getReportStatus(job).flag}`)}
                            </Typography.Text>
                        </Flex>
                    </Flex>
                </Flex>

                <JobStatusTimeline
                    currentStatus={jobStatus.name}
                    marginTop={spacing[4]}
                    statuses={statuses}
                    handleStatusChange={handleStatusChange}
                    isSuperAdmin={checkForSuperAdmin(currentUser)}
                    savedStatus={job?.status?.name}
                    reportReadiness={reportReadiness}
                />

                <Divider style={{ margin: `${spacing[4]} 0 0 0` }} />

                <Flex vertical gap={spacing[4]} style={{ marginBottom: spacing[8] }}>
                    <DragAndDropButton
                        title={t('jobPreview:report')}
                        fileTypes={['application/pdf']}
                        isMultiple={false}
                        files={files.serverFiles?.filter(
                            file => file.fileName.includes(Flag.Report) && !file.isWatermark
                        )}
                        onFileSelect={selectedFile =>
                            setFiles(prev => ({
                                ...prev,
                                selectedFiles: { ...prev.selectedFiles, report: selectedFile },
                            }))
                        }
                        deleteFile={onClickDelete}
                        isRequired={true}
                        currentFile={files.selectedFiles.report}
                        jobStatus={originalJobStatus}
                    />
                    <DragAndDropButton
                        title={t('jobPreview:kompl')}
                        fileTypes={['application/pdf']}
                        isMultiple={false}
                        files={files.serverFiles?.filter(
                            file => file.fileName.includes(Flag.Kompl) && !file.isWatermark
                        )}
                        onFileSelect={selectedFile =>
                            setFiles(prev => ({
                                ...prev,
                                selectedFiles: { ...prev.selectedFiles, kompl: selectedFile },
                            }))
                        }
                        deleteFile={onClickDelete}
                        isRequired={false}
                        currentFile={files.selectedFiles.kompl}
                    />

                    {isExtraSmallDevice ? (
                        <div
                            style={{
                                position: 'sticky',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                backgroundColor: 'transparent',
                                zIndex: 10,
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Button type="primary" size="large" onClick={handleSave} loading={loading.uploadingFiles}>
                                {t('jobs:actions.save')}
                            </Button>
                        </div>
                    ) : (
                        <Form form={form} onFinish={handleSave}>
                            <Flex justify="flex-end" gap={spacing[3]}>
                                <Form.Item>
                                    <Button
                                        type="primary"
                                        size="large"
                                        htmlType="submit"
                                        style={{ alignSelf: 'flex-end' }}
                                        loading={loading.uploadingFiles}
                                    >
                                        {t('jobs:actions.save')}
                                    </Button>
                                </Form.Item>
                            </Flex>
                        </Form>
                    )}
                </Flex>

                {/* uncomment next block of code when Audit Log will be ready  */}
                {/* <Divider />

    </div>          {c
                }heckForSuperAdmin(currentUser) && (
                    <Flex gap={spacing[1]} vertical>
                        <Typography.Text>{t('activity')}</Typography.Text>
                        <PreviewJobTabs selectedTab={selectedTab} onChange={setSelectedTab} />
                    </Flex>
                )}

                {checkForSuperAdmin(currentUser) && (
                    <div
                        id="scrollableDiv"
                        style={{
                            height: 408,
                            overflow: 'auto',
                        }}
                    >
                        <InfiniteScroll
                            ref={infiniteScrollRef}
                            dataLength={jobLogs.length}
                            next={fetchLogs}
                            hasMore={hasMore}
                            height={408}
                            loader={
                                <Skeleton
                                    avatar
                                    paragraph={{
                                        rows: 1,
                                    }}
                                    active
                                />
                            }
                            onScroll={handleScroll}
                            style={{ scrollbarWidth: 'thin', scrollbarColor: '#888 #f1f1f1' }}
                        >
                            <LogsList logs={jobLogs} entityName={'jobs'} loading={loading.logs} />
                        </InfiniteScroll>
                    </div>
                )} */}
            </Flex>
        </Card>
    );
};
